import { Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { dsDtos, userDtos, webDtos } from '@vectronic/openApi-admin';
import { HistoryItem as DeviceHistoryItem, DialogService, HistoryItemType } from '@vectronic/util';
import moment from 'moment';
import { combineLatest } from 'rxjs';
import {
  HistoryComponent,
  HistoryItem,
  HistoryType,
} from '../../components/history/history.component';
import { ParameterDialogComponent } from '../../components/parameter-dialog/parameter-dialog.component';

@Component({
  selector: 'vectronic-device-history-card',
  templateUrl: './device-history-card.component.html',
  styleUrls: ['./device-history-card.component.scss'],
  imports: [HistoryComponent],
})
export class DeviceHistoryCardComponent {
  private dialogService = inject(DialogService);

  title = input('Device History');
  comments = input<webDtos.Comment[] | undefined>();
  history = input<dsDtos.DeviceChange[] | undefined>();
  commands = input<dsDtos.Command[] | undefined>();
  mortalities = input<webDtos.Mortality[] | undefined>();
  mortalityImplants = input<webDtos.MortalityImplant[] | undefined>();
  separations = input<webDtos.Separation[] | undefined>();
  vaginalImplants = input<webDtos.VaginalImplant[] | undefined>();
  virtualFences = input<webDtos.VirtualFence[] | undefined>();
  userAccounts = input.required<userDtos.UserAccount[]>();
  parameters = input.required<dsDtos.Parameter[]>();
  categories = input.required<dsDtos.ParameterCategory[]>();
  subsystems = input.required<dsDtos.Subsystem[]>();
  deviceClasses = input.required<dsDtos.DeviceClass[]>();
  device = input.required<dsDtos.Device>();
  deviceTypes = input.required<dsDtos.DeviceType[]>();
  deviceTypeParameterConfig = input<dsDtos.DeviceTypeParameterConfigWithAvailableList[]>();
  commandCreators = input<(dsDtos.CommandCreator & { label: string })[] | undefined>();
  canModifyComments = input.required<boolean>();

  openScheduleModal = output<{
    schedules: dsDtos.Schedule[];
    idDevice: number;
  }>();
  openVirtualFenceModal = output<{
    idDevice: number;
    virtualFenceConstructionRules: dsDtos.VirtualFenceConstructionRule[];
  }>();

  items = signal<DeviceHistoryItem[]>([]);
  selectedType = signal<'all' | HistoryItemType>('all');

  historyItems = computed<HistoryItem[]>(() =>
    this.items().map((item) => ({
      id: item.id,
      author: this.itemAuthor(item),
      timestamp: item.date,
      type: item.type,
      text: item.text ?? '',
      actions:
        item.type === 'command'
          ? [
              ...(item.command?.schedules?.length
                ? [{ id: 'schedules', icon: 'settings_remote', tooltip: 'View Schedules' }]
                : []),
              ...(item.command?.parameterDataList?.length
                ? [{ id: 'parameters', icon: 'tune', tooltip: 'View Parameters' }]
                : []),
              ...(item.command?.virtualFenceConstructionRules?.length
                ? [{ id: 'virtual-fence', icon: 'gps_fixed', tooltip: 'View Virtual Fence' }]
                : []),
            ]
          : [],
    })),
  );

  types: HistoryType[] = [
    { value: 'all', label: 'All' },
    { value: 'event', label: 'Event' },
    { value: 'change', label: 'History' },
    { value: 'command', label: 'Command' },
    { value: 'comment', label: 'Comment' },
  ];

  constructor() {
    combineLatest([
      toObservable(this.comments),
      toObservable(this.history),
      toObservable(this.mortalities),
      toObservable(this.mortalityImplants),
      toObservable(this.separations),
      toObservable(this.vaginalImplants),
      toObservable(this.virtualFences),
      toObservable(this.commands),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateItems());
  }

  /** Update items signal based on inputs */
  updateItems() {
    const items: DeviceHistoryItem[] = [];
    this.comments()?.forEach((comment) =>
      items.push({
        type: 'comment',
        comment,
        date: comment.date,
        text: comment.text,
        id: comment.date,
      }),
    );
    this.history()?.forEach((history) =>
      items.push({
        type: 'change',
        history,
        date: history.date,
        text: history.text,
        id: history.date,
      }),
    );
    this.mortalities()?.forEach((mortality) =>
      items.push({
        type: 'event',
        event: mortality,
        date: mortality.acquisitionTime,
        text: 'Mortality detected',
        id: mortality.acquisitionTime,
      }),
    );
    this.mortalityImplants()?.forEach((mortalityImplant) =>
      items.push({
        type: 'event',
        event: mortalityImplant,
        date: mortalityImplant.acquisitionTime,
        text: 'Mortality Implants detected',
        id: mortalityImplant.acquisitionTime,
      }),
    );
    this.separations()?.forEach((separation) =>
      items.push({
        type: 'event',
        event: separation,
        date: separation.acquisitionTime,
        text: `Separation ${separation.extendedMessageType} for Sensor ${separation.idSensor}`,
        id: separation.acquisitionTime,
      }),
    );
    this.vaginalImplants()?.forEach((vaginalImplant) =>
      items.push({
        type: 'event',
        event: vaginalImplant,
        date: vaginalImplant.acquisitionTime,
        text: `Vaginal Implant ${vaginalImplant.extendedMessageType} for Sensor ${vaginalImplant.idSensor}`,
        id: vaginalImplant.acquisitionTime,
      }),
    );
    this.virtualFences()?.forEach((virtualFence) =>
      items.push({
        type: 'event',
        event: virtualFence,
        date: virtualFence.acquisitionTime,
        text: `Virtual Fence ${virtualFence.vfEntered ? 'entered' : 'left'} - Lat: ${virtualFence.latitude}, Long: ${virtualFence.longitude}`,
        id: virtualFence.acquisitionTime,
      }),
    );
    this.commands()?.forEach((command) =>
      items.push({
        type: 'command',
        command: command,
        date: command.receivedResponseTime || command.sendToDeviceTime || command.insertTs,
        text: this.getCommandText(command),
        id: command.idCommand,
      }),
    );
    this.items.set(
      items.sort((a, b) => {
        const dateA = moment(a.date);
        const dateB = moment(b.date);
        if (dateA.isBefore(dateB)) return 1;
        if (dateA.isAfter(dateB)) return -1;
        return 0;
      }),
    );
  }

  itemAuthor(item: DeviceHistoryItem): string {
    if (item.type === 'change') return item.history?.authorName ?? item.history?.author ?? '';
    if (item.type === 'comment')
      return item.comment?.author.name || item.comment?.author.email || '';
    if (item.type === 'command') return this.commandCreatorLabel(item.command);
    return '';
  }

  getCommandText({
    schedules,
    parameterDataList,
    deviceCommand,
    virtualFenceConstructionRules,
  }: dsDtos.Command) {
    let string = '';
    if (schedules?.length) string += '-Schedules\n';
    if (parameterDataList?.length) string += '-Parameter\n';
    if (deviceCommand?.deviceCommandType) string += '-Command\n';
    if (virtualFenceConstructionRules?.length) string += '-Virtual Fence\n';
    return string;
  }

  commandCreatorLabel(cmd: dsDtos.Command | undefined): string {
    const id = cmd?.createdBy;
    if (!id) return '';
    const isAdmin = !!cmd?.adminCreator;
    const list = this.commandCreators();
    if (!list || !Array.isArray(list)) return '';
    const match =
      list.find((c) => c.creatorId === id && c.isAdmin === isAdmin) ||
      list.find((c) => c.creatorId === id);
    return match?.label || '';
  }

  onHistoryItemAction({ actionId, item }: { actionId: string; item: HistoryItem }) {
    const deviceItem = this.items().find(({ id }) => id === item.id);
    if (!deviceItem) return;

    if (actionId === 'schedules') this.onClickSchedule(deviceItem);
    if (actionId === 'parameters') this.onClickParameter(deviceItem);
    if (actionId === 'virtual-fence') this.onClickVirtualFence(deviceItem);
  }

  onClickSchedule({ command }: DeviceHistoryItem) {
    this.openScheduleModal.emit({
      schedules: command?.schedules,
      idDevice: command?.idDevice,
    });
  }
  onClickVirtualFence({ command }: DeviceHistoryItem) {
    this.openVirtualFenceModal.emit({
      idDevice: command?.idDevice,
      virtualFenceConstructionRules: command?.virtualFenceConstructionRules,
    });
  }

  onClickParameter(selectedItem: DeviceHistoryItem) {
    const deviceType = this.deviceTypes().find(
      (deviceType) => deviceType.id === this.device().deviceTypeId,
    );
    const deviceClass = this.deviceClasses()?.find(
      (deviceClass) => deviceClass.id === deviceType?.deviceClassId,
    );
    this.dialogService.openWide(ParameterDialogComponent, {
      data: {
        parameterDataList: selectedItem.command?.parameterDataList,
        parameters: this.parameters(),
        categories: this.categories(),
        subsystems: this.subsystems(),
        idDevice: this.device()?.idDevice,
        deviceName: deviceClass?.name,
        deviceTypeParameterConfig: this.deviceTypeParameterConfig(),
      },
    });
  }
}
